import { calculateContractAmounts } from './calculateContractAmounts'

export type Tax = {
  amount?: number
  appliesTo?: string[]
  id?: string
  includedInPrice?: boolean
  name: string
  order?: number
  percentage?: number
  showInAdvertisedPrice?: boolean
}

type IncludedTax = {
  name: string
  percentage: number
}

type NonIncludedTax = {
  amountFee: number
  name: string
}

type CompoundTaxResult = {
  advertisingAmount: number
  includedTaxes: IncludedTax[]
  nonIncludedTaxes: NonIncludedTax[]
  totalAmount: number
}

/**
 * Checks if taxes have compound features (order, appliesTo, showInAdvertisedPrice)
 */
export const hasCompoundTaxFeatures = (taxes: Tax[]): boolean => {
  return taxes.some(
    tax =>
      tax.id &&
      (tax.order !== undefined ||
        tax.appliesTo !== undefined ||
        tax.showInAdvertisedPrice !== undefined),
  )
}

/**
 * Applies compound tax calculation to a base amount.
 */
export const applyCompoundTaxesToAmount = (
  baseAmount: number,
  taxes: Tax[],
): CompoundTaxResult => {
  if (baseAmount <= 0 || taxes.length === 0) {
    return {
      advertisingAmount: baseAmount,
      includedTaxes: [],
      nonIncludedTaxes: [],
      totalAmount: baseAmount,
    }
  }

  // Convert to ContractTaxLine format (same as dashboard does)
  const contractTaxes = taxes.map(tax => {
    const base = {
      id: tax.id || tax.name,
      includedInPrice: Boolean(tax.includedInPrice),
      showInAdvertisedPrice: Boolean(tax.showInAdvertisedPrice),
      order: tax.order ?? undefined,
    }

    if (typeof tax.amount === 'number') {
      return { ...base, amount: tax.amount }
    }

    return {
      ...base,
      percentage: tax.percentage,
      appliesTo: Array.isArray(tax.appliesTo) ? tax.appliesTo : undefined,
    }
  })

  // Calculate compound taxes
  const calculation = calculateContractAmounts(baseAmount, contractTaxes)

  // Convert breakdown back to tax arrays
  // Create a lookup map for efficient access
  const taxLookup = new Map(taxes.map(tax => [tax.id || tax.name, tax]))

  const includedTaxes = calculation.breakdown
    .map(detail => {
      const originalTax = taxLookup.get(detail.id)
      return originalTax?.includedInPrice
        ? {
            name: originalTax.name,
            percentage: originalTax.percentage || 0,
          }
        : null
    })
    .filter((tax): tax is IncludedTax => tax !== null)

  const nonIncludedTaxes = calculation.breakdown
    .map(detail => {
      const originalTax = taxLookup.get(detail.id)
      return originalTax &&
        !originalTax.includedInPrice &&
        detail.computedAmount > 0
        ? {
            name: originalTax.name,
            amountFee: detail.computedAmount,
          }
        : null
    })
    .filter((tax): tax is NonIncludedTax => tax !== null)

  return {
    advertisingAmount: calculation.advertisingAmount,
    includedTaxes,
    nonIncludedTaxes,
    totalAmount: calculation.totalAmount,
  }
}
